import { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { Check, PhoneCall } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, Eyebrow, MaskedLine } from "@/components/Reveal";
import CtaBand from "@/components/home/CtaBand";
import { FINANCE_PAGES, FINANCE_PAGE_LABELS } from "@/data/financePages";

const DEFAULT_TITLE = "Commercial Mortgage Broker Melbourne | Invesmo";
const DEFAULT_DESC = "Explore commercial property, development and practice finance with Invesmo. Finance enquiries for doctors, accountants and professionals in Melbourne.";

export default function FinancePage({ slug }) {
  const page = FINANCE_PAGES[slug];

  useEffect(() => {
    if (!page) return;
    document.title = page.metaTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", page.metaDescription);
    return () => {
      document.title = DEFAULT_TITLE;
      if (meta) meta.setAttribute("content", DEFAULT_DESC);
    };
  }, [page]);

  if (!page) return <Navigate to="/services" replace />;

  const others = Object.entries(FINANCE_PAGE_LABELS).filter(([s]) => s !== slug);

  return (
    <div data-testid={`finance-page-${slug}`}>
      <section className="pt-36 sm:pt-44 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>{page.eyebrow}</Eyebrow>
            <h1 className="font-display font-extrabold tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-6xl text-ink max-w-4xl">
              <MaskedLine delay={0.15}>{page.h1[0]}</MaskedLine>
              <MaskedLine delay={0.3}>{page.h1[1]}</MaskedLine>
              <MaskedLine delay={0.45}>
                <span className="font-editorial italic font-medium text-transparent bg-clip-text bg-gradient-to-r from-gold-500 to-brand">
                  {page.h1[2]}
                </span>
              </MaskedLine>
            </h1>
            <Reveal delay={0.55}>
              <p className="mt-8 text-base sm:text-lg text-ink/60 max-w-2xl leading-relaxed">{page.intro}</p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to={`/callback?utm_source=${slug}`}
                  data-testid="finance-callback-cta"
                  className="group rounded-full bg-obsidian text-gold px-8 py-4 text-sm font-semibold tracking-wide inline-flex items-center gap-3 transition-all duration-300 hover:bg-gold hover:text-obsidian hover:shadow-gold-glow"
                >
                  <PhoneCall size={15} /> Request a callback
                </Link>
                <Link
                  to="/book"
                  data-testid="finance-book-cta"
                  className="rounded-full border border-ink/15 px-8 py-4 text-sm font-semibold tracking-wide text-ink transition-all duration-300 hover:border-gold hover:text-gold-700"
                >
                  Book a meeting
                </Link>
              </div>
            </Reveal>
          </Reveal>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-2">
            <div className="rounded-3xl bg-obsidian text-white p-8 sm:p-10 grain relative overflow-hidden h-full" data-testid="finance-bullets">
              <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-gold/15 blur-3xl pointer-events-none" />
              <h2 className="font-display text-xl font-bold text-gold mb-7">{page.bulletTitle}</h2>
              <ul className="space-y-4">
                {page.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-white/75 leading-relaxed">
                    <Check size={16} className="text-gold mt-0.5 shrink-0" strokeWidth={2.5} />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <div className="lg:col-span-3 space-y-6">
            {page.body.map((p, i) => (
              <Reveal key={i} delay={0.08 * i}>
                <p className="text-ink/60 leading-relaxed text-[15px] sm:text-base">{p}</p>
              </Reveal>
            ))}
            <Reveal delay={0.2}>
              <p className="text-[11px] text-ink/40 leading-relaxed pt-2">
                *Higher LVRs, LMI waivers and preferential pricing are subject to lender policy, eligibility and credit
                assessment. All applications subject to lender approval. Terms, conditions, fees and charges apply.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-8">Common questions</h2>
          </Reveal>
          <Accordion type="single" collapsible className="space-y-4">
            {page.faqs.map((f, i) => (
              <Reveal key={f.q} delay={0.05 * i}>
                <AccordionItem
                  value={`faq-${i}`}
                  data-testid={`finance-faq-${i}`}
                  className="rounded-2xl border border-ink/8 bg-white px-6 sm:px-8 overflow-hidden transition-all duration-300 data-[state=open]:border-gold/50"
                >
                  <AccordionTrigger className="hover:no-underline py-6 text-left">
                    <span className="font-display text-base sm:text-lg font-semibold text-ink">{f.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-sm sm:text-[15px] text-ink/60 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              </Reveal>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="pb-20 sm:pb-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-ink/40 mb-5">Explore more specialist finance</h3>
            <div className="flex flex-wrap gap-3">
              {others.map(([s, label]) => (
                <Link
                  key={s}
                  to={`/${s}`}
                  data-testid={`finance-related-${s}`}
                  className="rounded-full border border-ink/10 bg-white px-5 py-2.5 text-xs font-semibold text-ink/60 transition-all duration-300 hover:border-gold hover:text-gold-700"
                >
                  {label}
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
